import { Group, Stack, Text } from "@mantine/core"
import { ProgressBox, Select } from "@tfg/ui"
import { useMemo, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { useDashboardStats } from "~/pages/Brand/Home/hooks/useDashboardStats"
import { useFilteredData } from "~/pages/Brand/Home/hooks/useFilteredData"
import {
  IngestionDateFilter,
  useIngestionDateFilter,
} from "~/pages/Brand/Home/hooks/useIngestionDateFilter"

interface Props {
  products: LibraryResponses.AgecComplianceProducts[]
}

export const AgecCompliance = ({ products }: Props) => {
  const { t } = useTranslation("home")

  const {
    options: ingestionDateOptions,
    ingestionDateFilter,
    setIngestionDateFilter,
    satisfiesIngestionDateFilter,
  } = useIngestionDateFilter()

  const [brandTypeFilter, setBrandTypeFilter] = useState<string | null>(null)
  const [collection, setCollection] = useState<string | null>(null)

  const filteredProducts = useFilteredData({
    data: products,
    filters: [
      (product) => {
        return collection ? product.collection === collection : true
      },
      (product) => {
        return brandTypeFilter ? product.brandType === brandTypeFilter : true
      },
      (product) => satisfiesIngestionDateFilter(product.ingestionDate),
    ],
  })

  const stats = useDashboardStats(
    filteredProducts,
    (product) => product.agecCompliance
  )

  const collections = useMemo(
    () =>
      [
        ...new Set<string>(
          products
            .filter((product) => product.collection)
            .map((product): string => {
              return product.collection ?? "Unkown"
            })
        ),
      ]
        .map((collection) => {
          return {
            value: collection,
            label: collection,
          }
        })
        .sort(),
    [products]
  )

  const brandTypeOptions = useMemo(
    () =>
      [...new Set(products.map((product) => product.brandType))]
        .flatMap((type) => (type ? [type] : []))
        .sort()
        .flatMap((val) => {
          const ptype = products.find((p) => p.brandType === val)
          return ptype
            ? [
                {
                  label: val,
                  value: val,
                  type: ptype.type.name,
                },
              ]
            : []
        }),
    [products]
  )

  return (
    <Stack>
      <Group wrap="nowrap">
        <Select
          clearable
          data={collections}
          value={collection}
          onChange={setCollection}
          placeholder={t("informationRequests.collection")}
        />
        <Select
          clearable
          data={brandTypeOptions}
          value={brandTypeFilter}
          onChange={setBrandTypeFilter}
          placeholder={t("informationRequests.type")}
        />
        <Select
          clearable
          data={ingestionDateOptions}
          value={ingestionDateFilter}
          onChange={(value) => {
            setIngestionDateFilter(value as IngestionDateFilter)
          }}
          placeholder={t("informationRequests.ingestionDate")}
        />
      </Group>
      {stats.total > 0 ? (
        <Stack bg="white" px="xs" py="md">
          <ProgressBox
            label={t("agecCompliance.label")}
            sections={[
              {
                color: "var(--progress-bar-color-filled)",
                textColor: "var(--progress-bar-filled-text)",
                value: stats.completedPercent,
                text: stats.completedCount.toString(),
                label: t("agecCompliance.compliant"),
                popover: (
                  <>
                    <Text>
                      <Trans
                        ns="home"
                        i18nKey="agecCompliance.compliant"
                        values={{
                          count: stats.completedCount,
                        }}
                        components={{
                          link1: (
                            <Link
                              to="/regulations/agec?agecCompliance=yes"
                              style={{
                                textDecoration: "underline",
                                color: "var(--mantine-color-blue-6)",
                              }}
                            />
                          ),
                        }}
                      >
                        <Link to="/regulations/agec?agecCompliance=yes">
                          {t("agecCompliance.compliant")}
                        </Link>
                      </Trans>
                    </Text>
                  </>
                ),
              },
              {
                color: "var(--progress-bar-color-unfilled)",
                textColor: "var(--progress-bar-unfilled-text)",
                value: stats.notCompletedPercent,
                text: stats.notCompletedCount.toString(),
                label: t("agecCompliance.nonCompliant"),
                popover: (
                  <>
                    <Text>
                      <Trans
                        ns="home"
                        i18nKey="agecCompliance.nonCompliant"
                        values={{
                          count: stats.notCompletedCount,
                        }}
                        components={{
                          link1: (
                            <Link
                              to="/regulations/agec?agecCompliance=no"
                              style={{
                                textDecoration: "underline",
                                color: "var(--mantine-color-blue-6)",
                              }}
                            />
                          ),
                        }}
                      />
                    </Text>
                  </>
                ),
              },
            ]}
            helpHoverText={<Text>{t("agecCompliance.totalText")}</Text>}
          />
          <Stack gap="0">
            <Text fz="sm">
              <Trans
                ns="home"
                i18nKey="agecCompliance.total"
                values={{
                  total: stats.total,
                }}
                components={{
                  strong1: (
                    <strong
                      style={{
                        color: "var(--progress-bar-color-filled)",
                      }}
                    />
                  ),
                }}
              />
            </Text>
            <Text fz="sm">
              <Trans
                ns="home"
                i18nKey="agecCompliance.complete"
                values={{
                  percent: stats.completedPercent,
                }}
                components={{
                  strong1: (
                    <strong
                      style={{
                        color: "var(--progress-bar-color-filled)",
                      }}
                    />
                  ),
                }}
              />
            </Text>
          </Stack>
        </Stack>
      ) : (
        <Text fz="sm">
          <strong>{t("informationRequests.noProducts")}</strong>
        </Text>
      )}
    </Stack>
  )
}
